import {
  Children,
  cloneElement,
  ReactElement,
  ReactNode,
  useState,
  useRef,
  RefAttributes,
  useEffect,
  RefObject
} from 'react';
import cn from 'classnames';
import styles from './styles.scss';
import { IconArrow } from 'components/Shared/Icon/themes/BGH-RD3/iconArrow';
import Overlay from 'components/Shared/Overlay';
import { Button } from 'components/Button';
import { format, NO_ELEMENT } from 'utils';
import { getQaDataAttr } from '@bygghemmait/utilities';
import { SelectInjectedProps, Option, OptionProps } from './option';
import { useToggle } from 'hooks';

export { LabeledSelect } from './labeledSelect';
export { Option };

export interface SelectClassNames {
  input?: string;
  icon?: string;
  selected?: string;
  optionList?: string;
}

export interface SelectProps {
  className?: string;
  inputClassName?: string;
  disabled?: boolean;
  placeHolder?: string;
  firstSelected?: boolean;
  inverted?: boolean;
  selectedValue: number | string | undefined;
  onChange: (value: number | string) => void;
  scrollable?: boolean;
  classNames?: SelectClassNames;
  labelFormat?: string;
  children: ReactNode;
}

type Direction = 'prev' | 'next' | 'unset' | 'pageup' | 'pagedown';
interface GetFocusedOptionIndexArgs {
  direction: Direction;
  current?: number;
  max: number;
}

const directions: { [key: string]: Direction } = {
  ArrowDown: 'next',
  ArrowUp: 'prev',
  PageUp: 'pageup',
  PageDown: 'pagedown'
};

const getFocusedOptionIndex = ({ direction, current, max }: GetFocusedOptionIndexArgs) => {
  if (current === undefined) {
    return direction === 'prev' ? max - 1 : 0;
  } else if (direction === 'next') {
    return current + 1 === max ? 0 : current + 1;
  } else if (direction === 'prev') {
    return current - 1 === -1 ? max - 1 : current - 1;
  }

  return 0;
};

interface SelectedScrollEffectOptions {
  focusedOption: { index: number; direction: Direction };
  numberOfOptions: number;
}
interface GetKeyboardScrollDirectionOptions extends SelectedScrollEffectOptions {
  listRect: DOMRect | ClientRect;
  focusedRect: DOMRect | ClientRect;
}
const getKeyboardScrollDirection = ({
  focusedOption,
  listRect,
  focusedRect,
  numberOfOptions
}: GetKeyboardScrollDirectionOptions): Direction => {
  const scrollToTop =
    focusedOption.index === 0 &&
    (focusedOption.direction === 'next' || focusedOption.direction === 'pageup');
  if (scrollToTop) {
    return 'pageup';
  }

  const scrollToBottom =
    focusedOption.index === numberOfOptions - 1 &&
    (focusedOption.direction === 'prev' || focusedOption.direction === 'pagedown');
  if (scrollToBottom) {
    return 'pagedown';
  }

  const scrollDown = focusedRect.height + listRect.bottom === focusedRect.bottom;
  if (scrollDown) {
    return 'next';
  }

  const scrollUp = focusedRect.bottom === listRect.top;
  if (scrollUp) {
    return 'prev';
  }

  return 'unset';
};

const useSelectScrollEffect = (
  listRef: RefObject<HTMLUListElement>,
  { focusedOption, numberOfOptions }: SelectedScrollEffectOptions
) => {
  const focusedRef = useRef<HTMLElement>(NO_ELEMENT);

  useEffect(() => {
    if (focusedOption.direction === 'unset' || !focusedRef.current || !listRef.current) {
      return;
    }

    const listRect = listRef.current.getBoundingClientRect();
    const focusedRect = focusedRef.current.getBoundingClientRect();

    const direction = getKeyboardScrollDirection({
      listRect,
      focusedRect,
      focusedOption,
      numberOfOptions
    });

    if (direction === 'pageup') {
      listRef.current.scrollTo(0, 0);
    } else if (direction === 'pagedown') {
      listRef.current.scrollBy(0, focusedRect.height * numberOfOptions);
    } else if (direction === 'next') {
      listRef.current.scrollBy(0, focusedRect.height);
    } else if (direction === 'prev') {
      listRef.current.scrollBy(0, -focusedRect.height);
    }
  }, [numberOfOptions, focusedOption, listRef]);

  return { focusedRef, listRef };
};

type OpenUpward = boolean;
interface OpenDirectionOptions {
  menuRef: RefObject<HTMLDivElement>;
  listRef: RefObject<HTMLUListElement>;
  open: boolean;
}
const useOpenDirection = ({ menuRef, listRef, open }: OpenDirectionOptions): [OpenUpward] => {
  const [upwards, setUpwards] = useToggle(false);
  useEffect(() => {
    if (!menuRef.current || !listRef.current || !open) {
      return;
    }
    const containerRect = menuRef.current.getBoundingClientRect();
    const menuHeight = listRef.current.clientHeight;

    const spaceAtBottom =
      document.documentElement.clientHeight - containerRect.top - containerRect.height - menuHeight;
    const spaceAtTop = containerRect.top - menuHeight;

    const placeAbove = spaceAtBottom < 0 && spaceAtTop > spaceAtBottom;
    if (placeAbove !== upwards) {
      setUpwards(placeAbove);
    }
  }, [open, listRef, menuRef, setUpwards, upwards]);
  return [upwards];
};

export const Select = ({
  className,
  classNames = {},
  inverted,
  disabled,
  placeHolder,
  labelFormat,
  scrollable,
  selectedValue,
  onChange,
  children
}: SelectProps) => {
  const numberOfOptions = Children.toArray(children).filter(
    (child: ReactElement<OptionProps>) => child.type === Option
  ).length;
  const menuRef = useRef<HTMLDivElement>(NO_ELEMENT);
  const [open, setOpen] = useToggle(false);
  const [focusedOption, setFocusedOption] = useState<{
    index: number;
    direction: Direction;
  }>({ index: 0, direction: 'unset' });
  const { focusedRef, listRef } = useSelectScrollEffect(useRef<HTMLUListElement>(NO_ELEMENT), {
    focusedOption,
    numberOfOptions
  });
  const [upwards] = useOpenDirection({ listRef, menuRef, open });
  let selectedOption = selectedValue?.toString() || placeHolder || '';

  const onOpen = () => !disabled && setOpen(true);
  const onClose = () => {
    setOpen(false);
    setFocusedOption({ index: -1, direction: 'unset' });
  };
  const handleSelect = (option: OptionProps) => () => {
    setOpen(false);
    onChange(option.value);
  };
  const clearFocusedOption = () => setFocusedOption({ index: -1, direction: 'unset' });

  const options = Children.map(children, (child: ReactElement<OptionProps>, i) => {
    if (child.type === Option) {
      const selected = child.props.value === selectedValue;
      const props = { ...child.props };
      if (selected) {
        selectedOption = labelFormat ? format(labelFormat, child.props.label) : child.props.label;
      }
      const isFocusedOption = i === focusedOption.index;

      if (isFocusedOption) {
        (props as RefAttributes<HTMLElement>).ref = focusedRef;
      }

      return cloneElement(child, {
        ...props,
        selected,
        labelFormat,
        className: cn(styles.option, child.props.className, {
          [styles.focused]: isFocusedOption
        }),
        role: 'option',
        onClick: handleSelect(child.props)
      } as SelectInjectedProps);
    } else {
      return child;
    }
  }) as ReactElement<
    OptionProps,
    string | React.JSXElementConstructor<Record<string, undefined>>
  >[];

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const spacebar = 32;
    if (e.key === 'Tab') {
      onClose();
      return;
    }
    e.preventDefault();
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      const index = getFocusedOptionIndex({
        current: focusedOption.index,
        max: options.length,
        direction: directions[e.key]
      });
      setFocusedOption({ index, direction: directions[e.key] });
      if (!open) {
        setOpen(true);
      }
    } else if (e.key === 'PageUp' || e.key === 'PageDown') {
      const index = e.key === 'PageUp' ? 0 : options.length - 1;
      setFocusedOption({ index, direction: directions[e.key] });
    } else if (e.key === 'Escape') {
      clearFocusedOption();
      setOpen(false);
    } else if (!open && e.keyCode === spacebar) {
      setOpen(true);
    } else if (e.key === 'Enter' || e.keyCode === spacebar) {
      const option = options.find((_, i) => i === focusedOption.index);
      if (option !== undefined) {
        handleSelect(option.props)();
      }
    }
  };

  const input = (
    <Button
      {...getQaDataAttr('listButton')}
      type="button"
      onClick={open ? onClose : onOpen}
      onKeyDown={handleKeyDown}
      className={cn(
        styles.input,
        styles.item,
        { [styles.open]: open, [styles.disabled]: disabled },
        classNames.input
      )}
      disabled={disabled}
    >
      <span
        className={cn(styles.selectedValue, classNames.selected)}
        dangerouslySetInnerHTML={{ __html: selectedOption }}
      />
      <span className={cn(styles.icon, classNames.icon)}>
        <IconArrow size="small" appearance="flip-90" />
      </span>
    </Button>
  );
  return (
    <Overlay transparent visible={open} onClick={onClose}>
      <div
        className={cn(
          styles.select,
          { [styles.inverted]: inverted, [styles.upwards]: upwards },
          className
        )}
        ref={menuRef}
      >
        {input}
        {!disabled && open && (
          <ul
            {...getQaDataAttr('quantityList')}
            role="listbox"
            ref={listRef}
            onMouseOver={clearFocusedOption}
            className={cn(styles.list, classNames.optionList, {
              [styles.open]: open,
              [styles.scrollable]: scrollable
            })}
          >
            {options}
          </ul>
        )}
      </div>
    </Overlay>
  );
};
